import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveIcon } from '../../lib/icons';
import { fetchAvatarImageDataUrl, type StorageSize } from '../../api/dataApi';
import { queryClient } from '../../lib/queryClient';

interface AvatarViewProps {
  symbol: string;
  colorHex: string;
  // The /storage key of an uploaded avatar image, if any (see
  // AvatarImagePicker.tsx) - when present and it resolves, takes
  // precedence over symbol+colorHex.
  imageKey?: string;
  size?: number;
  onClick?: () => void;
  // An uploaded image can be enlarged in three clicks: 1 zooms it in place (it
  // closes again when the mouse leaves), 2 opens it in a popup, 3 closes the
  // popup (Esc too). Only for avatars that are not inside a clickable row, since
  // the click is swallowed. No image, no zoom.
  zoomable?: boolean;
}

const PREVIEW = 320; // the zoomed image fits in this box, css px
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// The image's own width/height, so the zoom keeps its proportions.
function useNaturalSize(url: string): { w: number; h: number } | null {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setSize({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.src = url;
    return () => {
      alive = false;
    };
  }, [url]);
  return size;
}

// Click 1: the image expands out of the avatar's own box (scale transition), at
// its real aspect ratio and inside the viewport. pointer-events none, so the
// avatar underneath still gets the next click and the mouse-leave.
function ZoomedImage({ rect, url }: { rect: DOMRect; url: string }) {
  const natural = useNaturalSize(url);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!natural) return;
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, [natural]);
  if (!natural) return null;
  const fit = Math.min(PREVIEW / natural.w, PREVIEW / natural.h, 1.5);
  const w = Math.round(natural.w * fit);
  const h = Math.round(natural.h * fit);
  const left = clamp(rect.left + rect.width / 2 - w / 2, 8, Math.max(8, window.innerWidth - w - 8));
  const top = clamp(rect.top + rect.height / 2 - h / 2, 8, Math.max(8, window.innerHeight - h - 8));
  const from = Math.max(Math.min(rect.width / w, rect.height / h), 0.1);
  return createPortal(
    <img
      src={url}
      alt=""
      aria-hidden
      className="pointer-events-none fixed z-[60] rounded-2xl bg-white object-contain shadow-[0_12px_32px_rgba(0,0,0,0.25)] transition-[transform,opacity] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none dark:bg-[#151A21]"
      style={{
        left,
        top,
        width: w,
        height: h,
        transformOrigin: `${rect.left + rect.width / 2 - left}px ${rect.top + rect.height / 2 - top}px`,
        transform: open ? 'scale(1)' : `scale(${from})`,
        opacity: open ? 1 : 0.6,
      }}
    />,
    document.body
  );
}

function ImagePopup({ imageKey, fallback, onClose }: { imageKey: string; fallback: string; onClose: () => void }) {
  const large = useAvatarImageUrl(imageKey, 'large');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Avatar image"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
      className={`fixed inset-0 z-[70] flex cursor-zoom-out items-center justify-center bg-black/70 p-6 transition-opacity duration-200 motion-reduce:transition-none ${open ? 'opacity-100' : 'opacity-0'}`}
    >
      <img
        src={large ?? fallback}
        alt=""
        className={`max-h-[85vmin] max-w-[85vmin] rounded-2xl object-contain transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none ${open ? 'scale-100' : 'scale-90'}`}
      />
    </div>,
    document.body
  );
}

// Exported so templates (which show an emoji, not an AvatarView icon, as
// their fallback - see PublicTemplateScreen/TemplatesListScreen/
// TemplateManageScreen) can reuse the same fetch-and-cache logic without
// going through the full AvatarView component.
//
// Used to hold its own hand-rolled module-level Map<string, string | null>
// as a cache - replaced with the shared queryClient (see lib/queryClient.ts),
// which was already doing the identical job (dedup + cache) for every
// other network call this session. staleTime/gcTime: Infinity - an
// uploaded image's /storage key is content-addressed and immutable (see
// AvatarImagePicker.tsx), so unlike every other query in this app, a
// cached result here never needs a background refetch at all.
//
// `size` picks the rendition (see StorageSize): the 128 px thumb for small
// avatars, the 512 px medium otherwise - never the multi-MB original for a
// 40 px circle. Each size is its own cache entry.
export function useAvatarImageUrl(imageKey: string | undefined, size: StorageSize = 'medium'): string | null {
  const [url, setUrl] = useState<string | null>(
    imageKey ? (queryClient.getQueryData<string | null>(['avatarImage', imageKey, size]) ?? null) : null
  );

  useEffect(() => {
    if (!imageKey) {
      setUrl(null);
      return;
    }
    const cached = queryClient.getQueryData<string | null>(['avatarImage', imageKey, size]);
    if (cached !== undefined) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    queryClient
      .query({
        queryKey: ['avatarImage', imageKey, size],
        queryFn: () => fetchAvatarImageDataUrl(imageKey, size),
        staleTime: Infinity,
        gcTime: Infinity,
        // fetchStorageDataUrl already polls a still-scanning file; if that
        // gives up, don't cache the failure - try again on the next mount.
        retry: false,
      })
      .then((dataUrl) => {
        if (!cancelled) setUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [imageKey, size]);

  return url;
}

export function AvatarView({ symbol, colorHex, imageKey, size = 72, onClick, zoomable }: AvatarViewProps) {
  const Icon = resolveIcon(symbol);
  // 128 px thumb covers avatars up to 64 css px (2x displays included).
  const imageUrl = useAvatarImageUrl(imageKey, size <= 64 ? 'thumb' : 'medium');
  const canZoom = !!zoomable && !!imageKey && !!imageUrl;
  const box = useRef<HTMLDivElement>(null);
  // 0 = plain, 1 = zoomed in place, 2 = popup.
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const zoomUrl = useAvatarImageUrl(canZoom && stage > 0 ? imageKey : undefined, 'medium');

  const advance = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    if (stage === 0) {
      setRect(box.current?.getBoundingClientRect() ?? null);
      setStage(1);
    } else if (stage === 1) {
      setStage(2);
    } else {
      setStage(0);
    }
  };

  return (
    <>
      <div
        ref={box}
        onClick={canZoom ? advance : onClick}
        // Only the zoomed-in-place step closes when the mouse leaves; the popup stays.
        onMouseLeave={canZoom ? () => setStage((s) => (s === 1 ? 0 : s)) : undefined}
        role={onClick || canZoom ? 'button' : undefined}
        aria-label={canZoom ? 'View avatar image' : undefined}
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.28,
          background: imageUrl ? undefined : `${colorHex}2e`,
          cursor: canZoom ? (stage === 0 ? 'zoom-in' : 'pointer') : onClick ? 'pointer' : undefined,
          overflow: 'hidden',
        }}
        // A mid-grey wash behind uploaded images, so a white or a black logo
        // (transparent PNG) stays visible on both light and dark themes.
        className={`flex items-center justify-center shrink-0 ${imageUrl ? 'bg-gray-500/25' : ''}`}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon size={size * 0.42} strokeWidth={2.2} color={colorHex} />
        )}
      </div>
      {canZoom && stage === 1 && rect && (zoomUrl ?? imageUrl) && <ZoomedImage rect={rect} url={(zoomUrl ?? imageUrl)!} />}
      {stage === 2 && imageKey && <ImagePopup imageKey={imageKey} fallback={zoomUrl ?? imageUrl ?? ''} onClose={() => setStage(0)} />}
    </>
  );
}
